import React, {useCallback, useEffect, useState} from 'react';
import {useQuery} from "@tanstack/react-query";
import {FaChevronRight} from "react-icons/fa";
import {Template} from "../../../components/template/template.jsx";
import {Card} from "../../../components/card/card.jsx";
import Button from "../../../components/button/button.jsx";
import projectUtils from "../../../utils/project-utils.js";
import InstallerService from "../../../services/web/installer-service.js";
import InstallerSteps from "./components/installer-steps.jsx";
import InstallerCorporationStep from "./components/installer-corporation-step.jsx";
import InstallerUserStep from "./components/installer-user-step.jsx";
import InstallerMapModal from "./components/installer-map-modal.jsx";
import {errorMessage} from "./utils/installer-utils.js";
import Alert from "../../../components/alert/alert.jsx";

const initialForm = {
    name: "",
    city: "",
    address: "",
    number: "",
    neighborhood: "",
    phone: "",
    institutional_email: "",
    latitude: "",
    longitude: "",
};

const Installer = () => {
    const [step, setStep] = useState("corporation");
    const [form, setForm] = useState(initialForm);
    const [logo, setLogo] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [password, setPassword] = useState("");
    const [passwordConfirm, setPasswordConfirm] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [mapOpen, setMapOpen] = useState(false);
    const [restartRequired, setRestartRequired] = useState(false);

    const {data: state, isLoading: isLoadingState} = useQuery({
        queryKey: ["installer-state"],
        queryFn: InstallerService.state,
        retry: false,
    });

    // Already installed: the legacy index() redirected to "/" in this case.
    useEffect(() => {
        if (state?.installed) window.location.assign(import.meta.env.BASE_URL);
    }, [state?.installed]);

    // The entity may exist without the ADMIN user — resume at the user step.
    useEffect(() => {
        if (state?.hasEntity) setStep("user");
    }, [state?.hasEntity]);

    const handleInput = ({target}) => setForm(prev => ({...prev, [target.id]: target.value}));

    const handleCity = (option) => setForm(prev => ({
        ...prev,
        city: option?.value ?? "",
        latitude: option?.latitude ?? "",
        longitude: option?.longitude ?? "",
    }));

    const handleLogo = (file) => {
        if (!file) return;

        setLogo(file);

        const reader = new FileReader();
        reader.onload = (event) => setLogoPreview(event.target.result);
        reader.readAsDataURL(file);
    };

    const removeLogo = () => {
        setLogo(null);
        setLogoPreview(null);
    };

    const setCoordinates = useCallback((latitude, longitude) => {
        setForm(prev => ({...prev, latitude: String(latitude), longitude: String(longitude)}));
    }, []);

    const saveCorporation = () => {
        setIsLoading(true);

        const formData = new FormData();
        Object.entries(form).forEach(([key, value]) => formData.append(key, value ?? ""));
        if (logo) formData.append("logo-corporation", logo);

        InstallerService.saveCorporation(formData)
            .then(() => setStep("user"))
            .catch((error) => {
                console.error(error);
                projectUtils.swalErrorMessage("", errorMessage(error, "Não foi possível prosseguir."), "Ok");
            })
            .finally(() => setIsLoading(false));
    };

    const saveUser = () => {
        if (!passwordConfirm) {
            projectUtils.swalErrorMessage("", "O campo confirmação de senha é obrigatório.", "Ok");
            return;
        }

        if (passwordConfirm !== password) {
            projectUtils.swalErrorMessage("", "As senhas não conferem.", "Ok");
            return;
        }

        setIsLoading(true);

        const formData = new FormData();
        formData.append("password", password);

        InstallerService.saveUser(formData)
            .then(() => {
                setRestartRequired(true);
                projectUtils.swalMessage(
                    "warning",
                    "Reinicialização necessária",
                    "A instalação foi concluída. O serviço precisa ser reiniciado manualmente para aplicar as alterações.",
                    "Ok"
                );
            })
            .catch((error) => {
                console.error(error);
                projectUtils.swalErrorMessage("", errorMessage(error, "Não foi possível prosseguir."), "Ok");
            })
            .finally(() => setIsLoading(false));
    };

    return (
        <>
            <InstallerSteps step={step} />

            <div style={{marginLeft: "220px"}}>
                <Template isLoading={isLoading || isLoadingState}>
                    <Template.Header>
                        <Template.Title>
                            Instalador - <small>Entidade</small>
                        </Template.Title>
                    </Template.Header>

                    <Template.Body>
                        <Card>
                            <Card.Body className={"space-y-4"}>
                                {state && !state.licensed && (
                                    <Alert target={"danger"}>
                                        Licencie a máquina antes de prosseguir.
                                    </Alert>
                                )}

                                {restartRequired && (
                                    <Alert target={"alert"}>
                                        A instalação foi concluída. Reinicie o serviço manualmente para aplicar as alterações.
                                    </Alert>
                                )}

                                {!restartRequired && step === "corporation" && (
                                    <InstallerCorporationStep
                                        form={form}
                                        onChange={handleInput}
                                        onCity={handleCity}
                                        timezone={state?.timezone ?? ""}
                                        logoPreview={logoPreview}
                                        onLogoChange={handleLogo}
                                        onLogoRemove={removeLogo}
                                        onOpenMap={() => setMapOpen(true)}
                                    />
                                )}

                                {!restartRequired && step === "user" && (
                                    <InstallerUserStep
                                        password={password}
                                        passwordConfirm={passwordConfirm}
                                        onChange={({target}) => {
                                            if (target.id === "password") setPassword(target.value);
                                            else setPasswordConfirm(target.value);
                                        }}
                                    />
                                )}
                            </Card.Body>

                            {!restartRequired && (
                                <Card.Body className={"flex justify-end border-t border-[#e7eaec]"}>
                                    {step === "corporation" ? (
                                        <Button onClick={saveCorporation}>
                                            Próximo <FaChevronRight />
                                        </Button>
                                    ) : (
                                        <Button onClick={saveUser}>
                                            Finalizar <FaChevronRight />
                                        </Button>
                                    )}
                                </Card.Body>
                            )}
                        </Card>
                    </Template.Body>
                </Template>
            </div>

            <InstallerMapModal
                open={mapOpen}
                setOpen={setMapOpen}
                latitude={form.latitude}
                longitude={form.longitude}
                onSelect={setCoordinates}
            />
        </>
    );
};

export default Installer;
